import { useDevAgentStore } from "@/stores/devAgentStore";

const MAX_AUTO_TEST_RUNS = 2;
const TEST_CLASS_RE = /(Test|Tests)\b/;

async function queuePostSaveTests(testClassNames: string[]): Promise<void> {
  if (testClassNames.length === 0) return;
  const store = useDevAgentStore.getState();
  if (!store.sessionUuid || store.runningTest) return;
  const unique = [...new Set(testClassNames.filter(Boolean))].slice(0, MAX_AUTO_TEST_RUNS);
  for (const testClass of unique) {
    try {
      await useDevAgentStore.getState().runTest(testClass);
    } catch {
      /* store.runTest already records failures */
    }
  }
}

export async function runDevAgentPostSaveAnalysis(
  sessionUuid: string,
  data: { fileName: string; metadataType: string },
): Promise<void> {
  const { runPostSaveAnalysis } = await import("./devAgentService");
  const results = await runPostSaveAnalysis(sessionUuid, {
    class_name: data.fileName,
    metadata_type: data.metadataType,
  });
  const store = useDevAgentStore.getState();
  if (results.dependencies) {
    store.addDependency(results.dependencies);
  }
  if (results.impact) {
    store.addFeedItem({
      type: "impact_analysis",
      data: {
        class_name: data.fileName,
        blast_radius: results.impact.blast_radius,
        callers: results.impact.callers,
        callees: results.impact.callees,
        data_access: results.impact.data_access,
        triggers_at_risk: results.impact.triggers_at_risk,
        references: results.impact.references,
        summary: results.impact.summary,
      },
    });
  }
  if (results.coverage) {
    store.addFeedItem({
      type: "test_coverage",
      data: {
        class_name: data.fileName,
        test_classes: results.coverage.test_classes,
        aggregate_coverage_pct: results.coverage.aggregate_coverage_pct,
        summary: results.coverage.summary,
      },
    });
  }
  const autoTests = [
    ...(results.dependencies?.tests?.map((t) => t.name) || []),
    ...(results.coverage?.test_classes?.map((t) => t.name) || []),
  ].filter((name) => !!name && TEST_CLASS_RE.test(name) && name !== data.fileName);
  void queuePostSaveTests(autoTests);
}
